/** Tiny fetch-based API client.
 *
 * Responsibilities:
 *   - prepend baseUrl
 *   - inject Bearer token from a callback
 *   - JSON encode/decode
 *   - surface backend error envelopes
 *   - support AbortSignal
 *
 * Does NOT retry, does NOT cache, does NOT dedupe. That lives in
 * individual service modules and stores.
 */
import type { AdminErrorEnvelope, ApiClientConfig, ApiRequestOptions } from '@/types';

export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  readonly details: Record<string, string>;

  constructor(
    status: number,
    code: string,
    message: string,
    retryable: boolean,
    details: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.details = details;
  }
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: ApiRequestOptions['query'],
): string {
  const url = new URL(path, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export function createApiClient(config: ApiClientConfig) {
  async function request<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, query, signal } = opts;
    const url = buildUrl(config.baseUrl, path, query);
    const token = config.getToken();

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (opts.headers) Object.assign(headers, opts.headers);
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    if (res.status === 204) {
      return undefined as T;
    }

    let parsed: unknown;
    const text = await res.text();
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new AdminApiError(
          res.status,
          'CLIENT_BAD_JSON',
          `Non-JSON response (status ${res.status})`,
          false,
        );
      }
    }

    if (!res.ok) {
      const env = parsed as AdminErrorEnvelope | undefined;
      if (env && env.error) {
        if (res.status === 401) {
          config.onUnauthorized?.();
        }
        throw new AdminApiError(
          res.status,
          env.error.code,
          env.error.message,
          env.error.retryable,
          env.error.details ?? {},
        );
      }
      throw new AdminApiError(
        res.status,
        'CLIENT_UNKNOWN_ERROR',
        `HTTP ${res.status} — ${typeof parsed === 'object' && parsed !== null && 'detail' in parsed ? JSON.stringify((parsed as any).detail).slice(0, 300) : 'no detail'}`,
        res.status >= 500,
      );
    }

    return parsed as T;
  }

  return { request };
}
